import { useState } from 'react';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const sdkList = [
  {
    name: 'META66 Core SDK',
    lang: 'TypeScript',
    version: 'v4.2.1',
    desc: '元宇宙核心开发套件，包含场景构建、物理引擎、网络同步全套API。支持WebGL/WebGPU渲染管线，内置空间音频、碰撞检测、多人同步等核心模块。',
    stars: '12.8k',
    downloads: '2.3M',
    color: '#00FFCC',
    features: ['场景构建引擎', '物理碰撞系统', '多人网络同步', 'WebGPU渲染'],
  },
  {
    name: 'XR Interaction Kit',
    lang: 'C++/C#',
    version: 'v3.1.0',
    desc: 'VR/AR/MR交互开发框架，支持Unity/Unreal Engine深度集成。提供手势识别、眼动追踪、触觉反馈、空间锚点等XR核心能力。',
    stars: '9.4k',
    downloads: '1.8M',
    color: '#FF6B9D',
    features: ['手势识别', '眼动追踪', '触觉反馈', '空间锚点'],
  },
  {
    name: 'Web5 Protocol SDK',
    lang: 'JavaScript',
    version: 'v2.0.5',
    desc: 'Web5.0去中心化身份与数据协议实现，兼容主流区块链网络。内置DID身份管理、去中心化存储、跨链资产互操作等核心功能。',
    stars: '7.2k',
    downloads: '1.1M',
    color: '#FFD700',
    features: ['DID身份管理', '去中心化存储', '跨链互操作', '零知识证明'],
  },
  {
    name: 'Digital Twin Engine',
    lang: 'Python/Rust',
    version: 'v1.8.3',
    desc: '数字孪生建模与实时同步引擎，支持城市级别的孪生场景构建。提供IoT数据接入、实时仿真、预测分析等企业级能力。',
    stars: '5.6k',
    downloads: '680K',
    color: '#FF6B35',
    features: ['IoT数据接入', '实时仿真', '预测分析', '城市级建模'],
  },
  {
    name: 'Quantum Crypto Lib',
    lang: 'Rust',
    version: 'v1.2.0',
    desc: '量子加密算法库，提供后量子密码学安全保障，抵御量子计算攻击。实现CRYSTALS-Kyber、CRYSTALS-Dilithium等NIST标准算法。',
    stars: '4.1k',
    downloads: '420K',
    color: '#A855F7',
    features: ['后量子加密', 'NIST标准', '零信任架构', '量子密钥分发'],
  },
  {
    name: 'AI Agent Framework',
    lang: 'Python',
    version: 'v3.5.2',
    desc: '元宇宙AI智能体开发框架，支持自主学习、多智能体协作与情感模拟。内置LLM集成、强化学习、行为树等AI核心能力。',
    stars: '11.3k',
    downloads: '3.1M',
    color: '#00FF88',
    features: ['LLM集成', '强化学习', '多智能体', '情感模拟'],
  },
];

const langColors: Record<string, string> = {
  TypeScript: 'rgba(0,255,204,0.2)',
  'C++/C#': 'rgba(57,255,20,0.2)',
  JavaScript: 'rgba(255,215,0,0.2)',
  'Python/Rust': 'rgba(255,107,53,0.2)',
  Rust: 'rgba(168,85,247,0.2)',
  Python: 'rgba(0,255,136,0.2)',
};

const langTextColors: Record<string, string> = {
  TypeScript: '#00FFCC',
  'C++/C#': '#39FF14',
  JavaScript: '#FFD700',
  'Python/Rust': '#FF6B35',
  Rust: '#A855F7',
  Python: '#00FF88',
};

const toolchain = [
  {
    phase: '01 · 环境搭建',
    color: '#00FFCC',
    tools: [
      { name: 'META66 CLI', desc: '一键初始化项目脚手架，支持多种模板（元宇宙场景/XR应用/Web5 DApp）', icon: 'ri-terminal-line' },
      { name: 'DevKit Studio', desc: '可视化开发环境，内置场景编辑器、资产管理器、实时预览', icon: 'ri-layout-4-line' },
      { name: 'Simulator Pro', desc: '本地XR设备模拟器，无需真实硬件即可调试VR/AR应用', icon: 'ri-glasses-line' },
    ],
  },
  {
    phase: '02 · 开发调试',
    color: '#FFD700',
    tools: [
      { name: 'Scene Inspector', desc: '实时3D场景检查器，支持性能分析、碰撞可视化、网络状态监控', icon: 'ri-search-eye-line' },
      { name: 'Network Profiler', desc: '多人同步网络分析工具，可视化延迟、带宽、数据包丢失情况', icon: 'ri-wifi-line' },
      { name: 'AI Playground', desc: 'AI智能体行为测试沙盒，支持强化学习训练可视化', icon: 'ri-robot-line' },
    ],
  },
  {
    phase: '03 · 测试部署',
    color: '#FF6B9D',
    tools: [
      { name: 'META66 TestNet', desc: '专属测试网络，模拟6G超低延迟环境，支持压力测试', icon: 'ri-test-tube-line' },
      { name: 'Cloud Deploy', desc: '一键部署到META66全球节点，自动CDN加速与负载均衡', icon: 'ri-cloud-line' },
      { name: 'Analytics Hub', desc: '用户行为分析、性能监控、错误追踪一体化平台', icon: 'ri-bar-chart-line' },
    ],
  },
];

const incentives = [
  {
    icon: 'ri-trophy-line',
    title: '开发者激励基金',
    amount: '$5,000万',
    color: '#FFD700',
    desc: '专项基金支持优质开源项目，按贡献度自动分配USDT奖励。季度评审，年度最佳开发者额外奖励$100,000。',
    items: ['开源贡献奖励', '季度黑客松奖金', '年度最佳开发者', 'SDK使用量分成'],
  },
  {
    icon: 'ri-award-line',
    title: '开发者认证体系',
    amount: '4级认证',
    color: '#00FFCC',
    desc: '从Associate到Master，系统化认证体系提升职业竞争力。持证开发者优先获得项目合作机会与内推资格。',
    items: ['Associate认证（免费）', 'Professional认证', 'Expert认证', 'Master认证（最高荣誉）'],
  },
  {
    icon: 'ri-store-line',
    title: '应用市场分成',
    amount: '85%分成',
    color: '#FF6B9D',
    desc: '在META66应用市场发布的元宇宙应用，开发者获得85%收益分成，远超行业平均水平。支持多种变现模式。',
    items: ['付费应用85%分成', '订阅制收益分成', 'NFT资产交易分成', '广告收益分成'],
  },
  {
    icon: 'ri-group-line',
    title: '开发者社区',
    amount: '50万+成员',
    color: '#A855F7',
    desc: '全球最大的元宇宙开发者社区，提供技术支持、项目合作、人才招聘等全方位服务。每月线上/线下活动。',
    items: ['Discord技术社区', '每月技术分享会', '全球开发者峰会', '导师计划'],
  },
];

const docCategories = [
  { icon: 'ri-rocket-2-line', title: '快速入门', desc: '5分钟运行第一个元宇宙应用', pages: '12页', level: '入门', color: '#00FF88' },
  { icon: 'ri-cpu-line', title: '核心API参考', desc: '完整SDK API文档，含所有方法与参数', pages: '380页', level: '进阶', color: '#00FFCC' },
  { icon: 'ri-shield-keyhole-line', title: '安全开发规范', desc: '量子加密集成与数据隐私保护', pages: '56页', level: '进阶', color: '#FFD700' },
  { icon: 'ri-global-line', title: 'Web5.0协议规范', desc: 'DID身份协议与跨链互操作完整规范', pages: '124页', level: '高级', color: '#FF6B9D' },
  { icon: 'ri-glasses-line', title: 'XR开发手册', desc: 'VR/AR/MR场景构建与性能优化', pages: '200页', level: '进阶', color: '#A855F7' },
  { icon: 'ri-map-2-line', title: '数字孪生指南', desc: '从数据采集到孪生建模完整流程', pages: '88页', level: '高级', color: '#FF6B35' },
  { icon: 'ri-brain-line', title: 'AI智能体开发', desc: 'LLM集成、强化学习与多智能体协作', pages: '156页', level: '高级', color: '#00FF88' },
  { icon: 'ri-coin-line', title: 'Web5经济模型', desc: 'NFT、DeFi与元宇宙经济体系设计', pages: '92页', level: '进阶', color: '#FFD700' },
];

const devStats = [
  { icon: 'ri-code-s-slash-line', value: '50万+', label: '注册开发者', color: '#00FFCC' },
  { icon: 'ri-git-repository-line', value: '12万+', label: '开源项目', color: '#FFD700' },
  { icon: 'ri-download-cloud-line', value: '1.2亿+', label: 'SDK下载量', color: '#FF6B9D' },
  { icon: 'ri-trophy-line', value: '$5000万', label: '开发者激励基金', color: '#A855F7' },
];

const codeSnippet = `// META66 Core SDK — 快速开始
import { MetaVerse, Scene, Avatar } from '@meta66/core';
import { Web5Identity } from '@meta66/web5';
import { XRController } from '@meta66/xr';
import { QuantumCrypto } from '@meta66/quantum';

// 初始化量子加密元宇宙场景
const meta = new MetaVerse({
  network: '6G',
  protocol: 'web5',
  encryption: QuantumCrypto.CRYSTALS_KYBER,
  region: 'asia-pacific',
});

// 创建去中心化身份 (DID)
const identity = await Web5Identity.create({
  did: 'did:meta66:user123',
  credentials: ['verified-human', 'kyc-passed'],
});

// 进入元宇宙场景
const scene = await meta.joinScene('meta66-hub', {
  avatar: new Avatar({ identity, style: 'realistic' }),
  xr: XRController.auto(), // 自动适配VR/AR/桌面
  latency: 'ultra-low',    // 6G超低延迟模式
});

// 监听场景事件
scene.on('user-joined', (user) => {
  console.log(\`\${user.name} 进入了元宇宙！\`);
});

console.log('Welcome to META66 Metaverse! ✦');`;

type TabType = 'sdk' | 'toolchain' | 'incentive' | 'docs';

export default function DeveloperEcosystem() {
  const [activeTab, setActiveTab] = useState<TabType>('sdk');
  const [expandedSdk, setExpandedSdk] = useState<string | null>(null);
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: statsRef, isVisible: statsVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: contentRef, isVisible: contentVisible } = useScrollAnimation({ threshold: 0.05 });

  return (
    <section id="developer" className="relative py-24 md:py-32 overflow-hidden" style={{ background: '#030A14' }}>
      {/* Doodle background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-[700px] h-[700px] rounded-full opacity-5" style={{ background: 'radial-gradient(ellipse, #00FFCC 0%, transparent 70%)' }} />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full opacity-5" style={{ background: 'radial-gradient(ellipse, #A855F7 0%, transparent 70%)' }} />
        <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(rgba(0,255,200,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(0,255,200,0.02) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }} />
        {/* Doodle splats */}
        <div className="absolute top-32 right-20 w-40 h-40 opacity-8" style={{ background: 'radial-gradient(ellipse, rgba(255,215,0,0.15) 0%, transparent 70%)', borderRadius: '60% 40% 55% 45% / 45% 55% 40% 60%' }} />
        <div className="absolute bottom-40 left-16 w-32 h-32 opacity-8" style={{ background: 'radial-gradient(ellipse, rgba(255,107,157,0.12) 0%, transparent 70%)', borderRadius: '45% 55% 40% 60% / 60% 40% 55% 45%' }} />
      </div>

      {/* Doodle wave top */}
      <svg className="absolute top-0 left-0 w-full" height="6" viewBox="0 0 1440 6" preserveAspectRatio="none">
        <path d="M0,3 Q90,0 180,3 Q270,6 360,3 Q450,0 540,3 Q630,6 720,3 Q810,0 900,3 Q990,6 1080,3 Q1170,0 1260,3 Q1350,6 1440,3" stroke="rgba(0,255,200,0.2)" strokeWidth="1.5" fill="none"/>
      </svg>

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10">
        {/* Header */}
        <div
          ref={headerRef as React.RefObject<HTMLDivElement>}
          className={`text-center mb-16 transition-all duration-700 ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          <div
            className="inline-flex items-center gap-2 px-5 py-2 mb-6"
            style={{
              border: '1.5px dashed rgba(0,255,200,0.35)',
              borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%',
              background: 'rgba(0,255,200,0.04)',
            }}
          >
            <i className="ri-code-s-slash-line text-sm" style={{ color: '#00FFCC' }} />
            <span className="text-xs font-bold tracking-widest uppercase" style={{ color: '#00FFCC', fontFamily: 'Caveat, cursive' }}>Developer Ecosystem</span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-5" style={{ fontFamily: 'Orbitron, sans-serif' }}>
            开发者
            <span className="relative inline-block mx-3">
              <span className="text-transparent bg-clip-text" style={{ backgroundImage: 'linear-gradient(135deg, #00FFCC, #00AAFF)' }}>生态</span>
              <svg className="absolute -bottom-2 left-0 w-full" height="6" viewBox="0 0 100 6" preserveAspectRatio="none">
                <path d="M0,3 Q25,0 50,3 Q75,6 100,3" stroke="#00FFCC" strokeWidth="2" fill="none" strokeLinecap="round"/>
              </svg>
            </span>
          </h2>
          <p className="text-white/40 text-base max-w-3xl mx-auto leading-relaxed">
            为全球50万+开发者提供最完整的元宇宙开发工具链——从SDK到文档，从工具到激励，
            META66开发者生态是构建数字文明的技术基础
          </p>
        </div>

        {/* Dev Stats */}
        <div
          ref={statsRef as React.RefObject<HTMLDivElement>}
          className={`grid grid-cols-2 lg:grid-cols-4 gap-4 mb-16 transition-all duration-700 delay-150 ${statsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          {devStats.map((s) => (
            <div
              key={s.label}
              className="flex flex-col items-center gap-3 p-6 transition-all duration-300 hover:-translate-y-1"
              style={{
                border: `1.5px solid ${s.color}20`,
                borderRadius: '14px 10px 14px 10px / 10px 14px 10px 14px',
                background: `linear-gradient(135deg, ${s.color}06 0%, transparent 100%)`,
              }}
            >
              <div
                className="w-12 h-12 flex items-center justify-center"
                style={{
                  background: `${s.color}15`,
                  borderRadius: '50% 40% 60% 45% / 45% 60% 40% 55%',
                }}
              >
                <i className={`${s.icon} text-xl`} style={{ color: s.color }} />
              </div>
              <p className="text-2xl font-black" style={{ fontFamily: 'Orbitron, sans-serif', color: s.color }}>{s.value}</p>
              <p className="text-white/40 text-sm text-center">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Main Content - Code + Quick Links */}
        <div
          ref={contentRef as React.RefObject<HTMLDivElement>}
          className={`flex flex-col lg:flex-row gap-8 mb-16 transition-all duration-700 delay-200 ${contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          {/* Code Preview */}
          <div
            className="flex-1 rounded-2xl overflow-hidden"
            style={{
              border: '1.5px solid rgba(0,255,200,0.2)',
              background: '#010C08',
              boxShadow: '0 0 40px rgba(0,255,200,0.06)',
            }}
          >
            <div
              className="flex items-center justify-between px-5 py-3"
              style={{ borderBottom: '1px solid rgba(0,255,200,0.1)', background: 'rgba(0,0,0,0.4)' }}
            >
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500/60" />
                <div className="w-3 h-3 rounded-full bg-yellow-500/60" />
                <div className="w-3 h-3 rounded-full" style={{ background: 'rgba(0,255,200,0.6)' }} />
              </div>
              <span className="text-white/25 text-xs font-mono">meta66-quickstart.ts</span>
              <button
                className="flex items-center gap-1.5 px-3 py-1 text-xs font-semibold transition-colors duration-200 whitespace-nowrap cursor-pointer"
                style={{
                  background: 'rgba(0,255,200,0.08)',
                  border: '1px solid rgba(0,255,200,0.2)',
                  borderRadius: '6px 4px 8px 4px / 4px 8px 4px 6px',
                  color: '#00FFCC',
                }}
              >
                <i className="ri-file-copy-line text-xs" />
                复制
              </button>
            </div>
            <div className="p-5 overflow-x-auto">
              <pre className="text-xs leading-relaxed font-mono">
                {codeSnippet.split('\n').map((line, i) => (
                  <div key={i} className="flex gap-4">
                    <span className="text-white/12 select-none w-5 text-right flex-shrink-0 text-xs">{i + 1}</span>
                    <span className={
                      line.startsWith('//') ? 'text-white/25' :
                      line.includes('import') ? 'text-[#00FFCC]' :
                      line.includes('const') || line.includes('await') ? 'text-[#39FF14]' :
                      line.includes("'") || line.includes('"') || line.includes('`') ? 'text-[#FFD700]' :
                      line.includes(':') && !line.includes('//') ? 'text-[#FF6B9D]' :
                      'text-white/65'
                    }>{line || ' '}</span>
                  </div>
                ))}
              </pre>
            </div>
          </div>

          {/* Quick Links */}
          <div className="flex-1 flex flex-col gap-3">
            <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Caveat, cursive', fontSize: '1.4rem' }}>
              ✦ 快速开始
            </h3>
            {[
              { icon: 'ri-book-open-line', title: '开发者文档中心', desc: '完整API参考、教程与最佳实践，中英双语', badge: '中文版', color: '#00FFCC' },
              { icon: 'ri-github-line', title: 'GitHub 开源仓库', desc: '核心SDK与示例项目全部开源，欢迎PR', badge: '12.8k ⭐', color: '#FFD700' },
              { icon: 'ri-discord-line', title: 'Discord 开发者社区', desc: '加入50万+开发者的技术交流社区', badge: '50万+ 成员', color: '#A855F7' },
              { icon: 'ri-video-line', title: '视频教程库', desc: '从入门到精通的系统化视频课程，200+课时', badge: '200+ 课时', color: '#FF6B9D' },
              { icon: 'ri-award-line', title: '开发者认证考试', desc: '获取META66官方认证，提升职业竞争力', badge: '免费考试', color: '#00FF88' },
              { icon: 'ri-money-cny-circle-line', title: '激励计划申请', desc: '优质开源贡献可获得USDT奖励，季度评审', badge: '$5000万池', color: '#FF6B35' },
            ].map((item) => (
              <a
                key={item.title}
                href="#"
                className="group flex items-center gap-4 p-4 transition-all duration-300 cursor-pointer hover:-translate-y-0.5"
                style={{
                  border: `1px solid rgba(255,255,255,0.07)`,
                  borderRadius: '10px 8px 12px 8px / 8px 12px 8px 10px',
                  background: 'rgba(255,255,255,0.02)',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.borderColor = `${item.color}30`;
                  (e.currentTarget as HTMLElement).style.background = `${item.color}05`;
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.07)';
                  (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.02)';
                }}
              >
                <div
                  className="w-10 h-10 flex items-center justify-center flex-shrink-0 transition-all duration-300"
                  style={{
                    background: `${item.color}12`,
                    borderRadius: '50% 40% 60% 45% / 45% 60% 40% 55%',
                  }}
                >
                  <i className={`${item.icon} text-lg`} style={{ color: item.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-semibold">{item.title}</p>
                  <p className="text-white/35 text-xs mt-0.5">{item.desc}</p>
                </div>
                <span
                  className="px-2.5 py-1 text-xs font-semibold whitespace-nowrap flex-shrink-0"
                  style={{
                    background: `${item.color}12`,
                    border: `1px solid ${item.color}25`,
                    borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%',
                    color: item.color,
                    fontFamily: 'Caveat, cursive',
                  }}
                >
                  {item.badge}
                </span>
              </a>
            ))}
          </div>
        </div>

        {/* Tabs */}
        <div
          className="flex flex-wrap gap-1 p-1 mb-10 w-fit"
          style={{
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%',
          }}
        >
          {[
            { id: 'sdk', label: 'SDK 工具包', icon: 'ri-code-s-slash-line' },
            { id: 'toolchain', label: '开发工具链', icon: 'ri-tools-line' },
            { id: 'incentive', label: '激励机制', icon: 'ri-trophy-line' },
            { id: 'docs', label: '技术文档', icon: 'ri-book-open-line' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as TabType)}
              className="flex items-center gap-1.5 px-5 py-2.5 text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer"
              style={{
                borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%',
                background: activeTab === tab.id ? '#00FFCC' : 'transparent',
                color: activeTab === tab.id ? '#020612' : 'rgba(255,255,255,0.4)',
              }}
            >
              <i className={`${tab.icon} text-xs`} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* SDK Tab */}
        {activeTab === 'sdk' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {sdkList.map((sdk) => (
              <div
                key={sdk.name}
                className="group p-5 transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                style={{
                  border: `1.5px solid ${sdk.color}18`,
                  borderRadius: '14px 10px 14px 10px / 10px 14px 10px 14px',
                  background: `linear-gradient(135deg, ${sdk.color}06 0%, transparent 100%)`,
                }}
                onClick={() => setExpandedSdk(expandedSdk === sdk.name ? null : sdk.name)}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className="px-2 py-0.5 text-xs font-semibold"
                      style={{
                        background: langColors[sdk.lang] || 'rgba(255,255,255,0.1)',
                        color: langTextColors[sdk.lang] || 'rgba(255,255,255,0.5)',
                        borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%',
                        fontFamily: 'Caveat, cursive',
                      }}
                    >
                      {sdk.lang}
                    </span>
                    <span className="text-white/25 text-xs font-mono">{sdk.version}</span>
                  </div>
                  <div className="flex items-center gap-1 text-white/30 text-xs">
                    <i className="ri-star-line text-yellow-400/60" />
                    <span>{sdk.stars}</span>
                  </div>
                </div>
                <h4 className="text-white font-bold text-sm mb-2">{sdk.name}</h4>
                <p className="text-white/40 text-xs leading-relaxed mb-3">{sdk.desc}</p>

                {/* Features */}
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {sdk.features.map((f) => (
                    <span
                      key={f}
                      className="text-xs px-2 py-0.5"
                      style={{
                        background: `${sdk.color}10`,
                        border: `1px solid ${sdk.color}20`,
                        borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%',
                        color: `${sdk.color}`,
                        fontFamily: 'Caveat, cursive',
                        opacity: 0.8,
                      }}
                    >
                      {f}
                    </span>
                  ))}
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1 text-white/25 text-xs">
                    <i className="ri-download-line" style={{ color: `${sdk.color}60` }} />
                    <span>{sdk.downloads}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs font-semibold group-hover:gap-3 transition-all duration-300" style={{ color: sdk.color }}>
                    <span>下载 SDK</span>
                    <i className="ri-arrow-right-line" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Toolchain Tab */}
        {activeTab === 'toolchain' && (
          <div className="flex flex-col gap-8">
            {toolchain.map((phase) => (
              <div key={phase.phase}>
                <div className="flex items-center gap-3 mb-5">
                  <div
                    className="w-8 h-8 flex items-center justify-center text-xs font-black"
                    style={{
                      background: `${phase.color}20`,
                      border: `1.5px solid ${phase.color}40`,
                      borderRadius: '50% 40% 60% 45% / 45% 60% 40% 55%',
                      color: phase.color,
                      fontFamily: 'Orbitron, sans-serif',
                    }}
                  >
                    {phase.phase.split(' ')[0]}
                  </div>
                  <h3 className="text-white font-bold text-lg" style={{ fontFamily: 'Caveat, cursive', fontSize: '1.2rem', color: phase.color }}>
                    {phase.phase.split(' · ')[1]}
                  </h3>
                  <div className="flex-1 h-px" style={{ background: `${phase.color}20` }} />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {phase.tools.map((tool) => (
                    <div
                      key={tool.name}
                      className="p-5 transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                      style={{
                        border: `1.5px solid ${phase.color}15`,
                        borderRadius: '12px 8px 14px 10px / 10px 14px 8px 12px',
                        background: `${phase.color}05`,
                      }}
                    >
                      <div
                        className="w-10 h-10 flex items-center justify-center mb-3"
                        style={{
                          background: `${phase.color}15`,
                          borderRadius: '50% 40% 60% 45% / 45% 60% 40% 55%',
                        }}
                      >
                        <i className={`${tool.icon} text-lg`} style={{ color: phase.color }} />
                      </div>
                      <h4 className="text-white font-bold text-sm mb-2">{tool.name}</h4>
                      <p className="text-white/40 text-xs leading-relaxed">{tool.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Incentive Tab */}
        {activeTab === 'incentive' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {incentives.map((item) => (
              <div
                key={item.title}
                className="p-6 transition-all duration-300 hover:-translate-y-1"
                style={{
                  border: `1.5px solid ${item.color}20`,
                  borderRadius: '16px 12px 16px 12px / 12px 16px 12px 16px',
                  background: `linear-gradient(135deg, ${item.color}06 0%, transparent 100%)`,
                }}
              >
                <div className="flex items-start gap-4 mb-4">
                  <div
                    className="w-12 h-12 flex items-center justify-center flex-shrink-0"
                    style={{
                      background: `${item.color}15`,
                      borderRadius: '50% 40% 60% 45% / 45% 60% 40% 55%',
                    }}
                  >
                    <i className={`${item.icon} text-xl`} style={{ color: item.color }} />
                  </div>
                  <div>
                    <h4 className="text-white font-bold text-base">{item.title}</h4>
                    <p className="text-2xl font-black mt-1" style={{ fontFamily: 'Orbitron, sans-serif', color: item.color, fontSize: '1.3rem' }}>{item.amount}</p>
                  </div>
                </div>
                <p className="text-white/45 text-sm leading-relaxed mb-4">{item.desc}</p>
                <div className="flex flex-col gap-2">
                  {item.items.map((i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: item.color }} />
                      <span className="text-white/55 text-xs" style={{ fontFamily: 'Caveat, cursive', fontSize: '0.9rem' }}>{i}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Docs Tab */}
        {activeTab === 'docs' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {docCategories.map((doc) => (
              <a
                key={doc.title}
                href="#"
                className="group flex items-start gap-4 p-5 transition-all duration-300 cursor-pointer hover:-translate-y-0.5"
                style={{
                  border: `1px solid rgba(255,255,255,0.07)`,
                  borderRadius: '10px 8px 12px 8px / 8px 12px 8px 10px',
                  background: 'rgba(255,255,255,0.02)',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.borderColor = `${doc.color}30`;
                  (e.currentTarget as HTMLElement).style.background = `${doc.color}05`;
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.07)';
                  (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.02)';
                }}
              >
                <div
                  className="w-12 h-12 flex items-center justify-center flex-shrink-0"
                  style={{
                    background: `${doc.color}12`,
                    borderRadius: '50% 40% 60% 45% / 45% 60% 40% 55%',
                  }}
                >
                  <i className={`${doc.icon} text-xl`} style={{ color: doc.color }} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h4 className="text-white font-bold text-sm">{doc.title}</h4>
                    <span
                      className="px-2 py-0.5 text-xs font-semibold"
                      style={{
                        background: `${doc.color}15`,
                        color: doc.color,
                        borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%',
                        fontFamily: 'Caveat, cursive',
                      }}
                    >
                      {doc.level}
                    </span>
                  </div>
                  <p className="text-white/35 text-xs leading-relaxed mb-1">{doc.desc}</p>
                  <p className="text-white/20 text-xs">{doc.pages}</p>
                </div>
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <i className="ri-arrow-right-line text-sm" style={{ color: doc.color }} />
                </div>
              </a>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
